/**
 * H25 ทำเนียบยุทธภพ: weekly fame boards (data/fameBoards.ts). Scores come from progress (neigong levels, move mastery) and
 * from this week's marks (profession output, duel wins). NPC wanderers fill the boards with deterministic scores
 * (seeded by week, board, index). `setNow` (called from dayReset's applyRealDay) settles any finished week: expires last
 * week's rewards, then pays the rank from that week's final board: rank 1 gets a title (ฉายา) shown on the name tag and
 * an outfit for the week, ranks 2-3 gold. Weekly counters are scene marks `fame:<week>:<key>:<n>` so they survive saves.
 */
import { FAME, FAME_BOARDS, type FameBoardDef } from '../data/fameBoards';
import { WANDERER_NAMES } from '../data/wanderers';
import { TITLES } from '../data/titles';
import { mulberry32 } from '../rng';
import { realWeek } from '../dayReset';
import type { WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

export interface FameRow {
  readonly name: string;
  readonly score: number;
  readonly me: boolean;
}

const WEEK_MARK = 'fame-week:';
const TITLE_MARK = 'fame-title:';

const hashStr = (s: string): number => {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
};

/** The NPC wanderer's name (stable across weeks and boards). */
export function fameNpcName(i: number): string {
  const r = mulberry32(0x9e3779b1 ^ (i * 7919));
  const pick = <T>(a: readonly T[]): T => a[Math.floor(r() * a.length)]!;
  const given = r() < 0.5 ? WANDERER_NAMES.male : WANDERER_NAMES.female;
  return `${pick(WANDERER_NAMES.nickA)}${pick(WANDERER_NAMES.nickB)} ${pick(WANDERER_NAMES.family)}${pick(given)}`;
}

/** The NPC rows of a board in a week (deterministic). */
export function fameNpcRows(board: FameBoardDef, week: number): FameRow[] {
  const out: FameRow[] = [];
  for (let i = 0; i < FAME.npcCount; i++) {
    const r = mulberry32((hashStr(board.id) ^ Math.imul(week + 1, 2654435761) ^ Math.imul(i + 1, 40503)) >>> 0);
    r();
    const [lo, hi] = board.npc;
    out.push({ name: fameNpcName(i), score: lo + Math.floor(r() * r() * (hi - lo + 1)), me: false });
  }
  return out;
}

export class FameBoardsSystem implements WorldSystem {
  readonly name = 'fameBoards';
  week = 0;
  private titleClock = 0;

  constructor(private readonly h: SystemHost) {}

  private get pr() {
    return this.h.world.progress;
  }

  // ---- weekly counters (scene marks) ----
  private key(week: number, k: string): string {
    return `fame:${week}:${k}:`;
  }
  private count(week: number, k: string): number {
    const pre = this.key(week, k);
    const m = this.pr.scenes.find((s) => s.startsWith(pre));
    return m ? Number(m.slice(pre.length)) || 0 : 0;
  }
  add(k: string, n = 1): void {
    const sc = this.pr.scenes;
    const pre = this.key(this.week, k);
    const i = sc.findIndex((s) => s.startsWith(pre));
    const v = (i >= 0 ? Number(sc[i]!.slice(pre.length)) || 0 : 0) + n;
    if (i >= 0) sc[i] = pre + v;
    else sc.push(pre + v);
  }

  observe(e: WorldEvent): void {
    if (e.type === 'gathered' || e.type === 'crafted') {
      this.add(`prof:${e.prof}`, e.type === 'gathered' ? e.goods.reduce((a, g) => a + (g.n ?? 1), 0) || 1 : 1);
    } else if (e.type === 'pvpDuel' && e.phase === 'result') {
      const me = this.h.world.player.id;
      if (e.winner === me && (e.a === me || e.b === me)) this.add('duel');
    }
  }

  // ---- scores ----
  playerScore(board: string, week = this.week): number {
    const pr = this.pr;
    if (board === 'neigong') return Object.values(pr.neigong).reduce((a, b) => a + b, 0);
    if (board === 'arts') return Object.values(pr.skillLevels).reduce((a, b) => a + b, 0);
    return this.count(week, board);
  }

  standings(board: string, week = this.week): FameRow[] {
    const def = FAME_BOARDS.find((b) => b.id === board);
    if (!def) return [];
    const rows = [...fameNpcRows(def, week), { name: this.pr.name, score: this.playerScore(board, week), me: true }];
    return rows.sort((a, b) => b.score - a.score || Number(b.me) - Number(a.me));
  }

  /** The player's rank (1 = first) on a board, or 0 with no score. */
  rank(board: string, week = this.week): number {
    if (this.playerScore(board, week) <= 0) return 0;
    return this.standings(board, week).findIndex((r) => r.me) + 1;
  }

  // ---- rewards ----
  /** The active rank-1 titles (board ids). */
  titleBoards(): string[] {
    return this.pr.scenes.filter((s) => s.startsWith(TITLE_MARK)).map((s) => s.slice(TITLE_MARK.length));
  }
  /** The title text shown on the name tag, from the first active board. */
  titleText(): string | null {
    const b = this.titleBoards()[0];
    const d = b ? FAME_BOARDS.find((x) => x.id === b) : undefined;
    return d ? d.title : null;
  }

  private expire(): void {
    const pr = this.pr;
    pr.scenes = pr.scenes.filter((s) => !s.startsWith(TITLE_MARK));
    pr.skins = pr.skins.filter((s) => !s.startsWith(FAME.skinPrefix));
    if (pr.skinWorn.outfit?.startsWith(FAME.skinPrefix)) delete pr.skinWorn.outfit;
    const t = TITLES[pr.titleWorn];
    this.h.world.player.title = t ? t.name : pr.title;
  }

  private settle(week: number): void {
    const pr = this.pr;
    this.expire();
    for (const b of FAME_BOARDS) {
      const rk = this.rank(b.id, week);
      if (rk === 1) {
        pr.scenes.push(TITLE_MARK + b.id);
        const sk = FAME.skinPrefix + b.id;
        if (!pr.skins.includes(sk)) pr.skins.push(sk);
      } else if (rk === 2 || rk === 3) {
        pr.gold += FAME.gold[rk] ?? 0;
      }
    }
    const pre = 'fame:' + week + ':';
    pr.scenes = pr.scenes.filter((s) => !s.startsWith(pre));
  }

  /** Call with the clock (ms since the epoch); settles every week that finished since the last call. */
  realDay(_day: number, nowMs: number): void {
    this.setNow(nowMs);
  }
  setNow(now: number): void {
    const w = realWeek(now);
    const sc = this.pr.scenes;
    const i = sc.findIndex((s) => s.startsWith(WEEK_MARK));
    const stored = i >= 0 ? Number(sc[i]!.slice(WEEK_MARK.length)) : w;
    this.week = stored;
    if (w > stored) {
      this.settle(stored);
      this.week = w;
    }
    const mark = WEEK_MARK + this.week;
    const j = this.pr.scenes.findIndex((s) => s.startsWith(WEEK_MARK));
    if (j >= 0) this.pr.scenes[j] = mark;
    else this.pr.scenes.push(mark);
  }

  step(dt: number): void {
    this.titleClock -= dt;
    if (this.titleClock > 0) return;
    this.titleClock = 1;
    const t = this.titleText();
    if (t && this.h.world.player.title !== t) this.h.world.player.title = t;
  }
}
